import { useCallback, useRef, useState } from "react";
import { Button, useBrand } from "@fprint/ui";

import { CustomerHeader } from "../components/CustomerHeader";
import { StlDropzone } from "../components/StlDropzone";
import { useAuth } from "../lib/auth";
import {
  createCustomWork,
  presignCustomUpload,
  presignUpload,
  uploadCustomFile,
  uploadStl,
  type SupportingFileRef,
} from "../lib/api";
import { usePublicConfig } from "../lib/publicConfig";

const MAX_FILES = 10;
const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MiB — mirrors presign-custom

type UploadState =
  | { phase: "uploading" }
  | { phase: "uploaded"; key: string }
  | { phase: "error"; error: string };

interface FileEntry {
  id: number;
  file: File;
  state: UploadState;
}

let nextEntryId = 1;

/**
 * Custom-work request (always a Manual quote, login-first — the route is
 * wrapped in RequireAuth, so auth is loaded and signed-in here).
 *
 * Supporting files upload immediately on pick via presign-custom + the same
 * multipart-POST shape as the STL flow (fields first, `file` last); each file
 * tracks its own status with per-file retry/remove.
 */
export function CustomWork() {
  const brand = useBrand();
  const { getToken } = useAuth();
  const tokenGetter = useCallback(() => getToken(), [getToken]);
  const config = usePublicConfig();

  const [description, setDescription] = useState("");
  const [stlFile, setStlFile] = useState<File | null>(null);
  const [stlState, setStlState] = useState<UploadState | null>(null);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const filesInputRef = useRef<HTMLInputElement | null>(null);

  function updateEntry(id: number, state: UploadState): void {
    setFiles((prev) => prev.map((e) => (e.id === id ? { ...e, state } : e)));
  }

  async function uploadEntry(entry: FileEntry): Promise<void> {
    updateEntry(entry.id, { phase: "uploading" });
    try {
      const presign = await presignCustomUpload(entry.file.name, tokenGetter);
      await uploadCustomFile(presign, entry.file);
      updateEntry(entry.id, { phase: "uploaded", key: presign.key });
    } catch (err) {
      updateEntry(entry.id, {
        phase: "error",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  function addFiles(picked: FileList | null): void {
    if (!picked || picked.length === 0) return;
    setSubmitError(null);
    const incoming = Array.from(picked);
    const room = MAX_FILES - files.length;
    if (incoming.length > room) {
      setSubmitError(
        `You can attach at most ${MAX_FILES} supporting files (${room > 0 ? `${room} more` : "no more"} can be added).`,
      );
      return;
    }
    const entries: FileEntry[] = [];
    for (const file of incoming) {
      if (file.size > MAX_FILE_BYTES) {
        setSubmitError(
          `"${file.name}" is over 25 MB — supporting files must be 25 MB or smaller.`,
        );
        continue;
      }
      entries.push({ id: nextEntryId++, file, state: { phase: "uploading" } });
    }
    if (entries.length === 0) return;
    setFiles((prev) => [...prev, ...entries]);
    for (const entry of entries) void uploadEntry(entry);
  }

  async function handleStl(file: File): Promise<void> {
    setStlFile(file);
    setStlState({ phase: "uploading" });
    try {
      const presign = await presignUpload(file.name, tokenGetter);
      await uploadStl(presign, file);
      setStlState({ phase: "uploaded", key: presign.key });
    } catch (err) {
      setStlState({
        phase: "error",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const anyUploading =
    files.some((e) => e.state.phase === "uploading") ||
    stlState?.phase === "uploading";
  const anyFailed =
    files.some((e) => e.state.phase === "error") || stlState?.phase === "error";

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const desc = description.trim();
    if (!desc) {
      setSubmitError("Please describe the work you need.");
      return;
    }
    if (anyUploading) {
      setSubmitError("Please wait for uploads to finish.");
      return;
    }
    if (anyFailed) {
      setSubmitError(
        "Some files failed to upload — retry or remove them before submitting.",
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const refs: SupportingFileRef[] = files.flatMap((entry) =>
        entry.state.phase === "uploaded"
          ? [
              {
                s3_key: entry.state.key,
                filename: entry.file.name,
                size_bytes: entry.file.size,
              },
            ]
          : [],
      );
      await createCustomWork(
        {
          description: desc,
          stl_s3_key: stlState?.phase === "uploaded" ? stlState.key : null,
          files: refs,
        },
        tokenGetter,
      );
      setDone(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (config === null) {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col gap-6 px-6 py-12">
        <CustomerHeader title="Custom work" />
        <p className="text-muted-fg">Loading…</p>
      </main>
    );
  }

  if (!config.custom_work_enabled) {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col gap-6 px-6 py-12">
        <CustomerHeader title="Custom work" />
        <p className="text-muted-fg" data-testid="custom-work-disabled">
          Custom-work requests aren&apos;t available right now. You can still
          get an instant quote for a print-ready model.
        </p>
      </main>
    );
  }

  if (done) {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl flex-col gap-6 px-6 py-12">
        <CustomerHeader title="Request received" />
        <div
          role="status"
          data-testid="custom-work-done"
          className="rounded-md border border-success bg-success/10 px-4 py-3 text-sm text-success-fg"
        >
          Thanks — we&apos;ve received your custom-work request and will email
          your quote.
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-8 px-6 py-12">
      <CustomerHeader
        title="Custom work"
        subtitle={`Design, finishing, unusual materials or deadlines — describe what you need and ${brand.name} will email you a personal quote.`}
      />

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-6"
        data-testid="custom-work-form"
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">What do you need?</span>
          <textarea
            required
            data-testid="custom-work-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the work: design, finish, materials, deadlines…"
            className="min-h-[140px] rounded-md border border-border bg-bg px-2 py-1 text-sm"
            disabled={submitting}
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Model (optional)</span>
          {stlFile ? (
            <div className="flex items-center justify-between rounded-md border border-border bg-bg px-3 py-2 text-sm">
              <span data-testid="custom-work-stl-name">{stlFile.name}</span>
              <span className="flex items-center gap-2">
                {stlState?.phase === "uploading" && (
                  <span className="text-xs text-muted-fg">Uploading…</span>
                )}
                {stlState?.phase === "uploaded" && (
                  <span className="text-xs text-success-fg">Uploaded</span>
                )}
                {stlState?.phase === "error" && (
                  <>
                    <span className="text-xs text-danger-fg" role="alert">
                      {stlState.error}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleStl(stlFile)}
                      disabled={submitting}
                    >
                      Retry
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStlFile(null);
                    setStlState(null);
                  }}
                  disabled={submitting || stlState?.phase === "uploading"}
                >
                  Remove
                </Button>
              </span>
            </div>
          ) : (
            <StlDropzone onFile={(f) => void handleStl(f)} disabled={submitting} />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">
            Supporting files (optional — sketches, photos, drawings; up to{" "}
            {MAX_FILES} files, 25 MB each)
          </span>
          <input
            ref={filesInputRef}
            type="file"
            multiple
            data-testid="custom-work-files"
            className="text-sm"
            disabled={submitting || files.length >= MAX_FILES}
            onChange={(e) => {
              addFiles(e.target.files);
              // Allow re-picking the same file after a remove.
              e.target.value = "";
            }}
          />
          {files.length > 0 && (
            <ul className="flex flex-col gap-1" data-testid="custom-work-file-list">
              {files.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between rounded-md border border-border bg-bg px-3 py-2 text-sm"
                >
                  <span className="truncate">{entry.file.name}</span>
                  <span className="ml-3 flex shrink-0 items-center gap-2">
                    {entry.state.phase === "uploading" && (
                      <span className="text-xs text-muted-fg">Uploading…</span>
                    )}
                    {entry.state.phase === "uploaded" && (
                      <span className="text-xs text-success-fg">Uploaded</span>
                    )}
                    {entry.state.phase === "error" && (
                      <>
                        <span className="text-xs text-danger-fg" role="alert">
                          {entry.state.error}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void uploadEntry(entry)}
                          disabled={submitting}
                        >
                          Retry
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setFiles((prev) => prev.filter((f) => f.id !== entry.id))
                      }
                      disabled={submitting || entry.state.phase === "uploading"}
                    >
                      Remove
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {submitError && (
          <p
            role="alert"
            data-testid="custom-work-error"
            className="rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-danger-fg"
          >
            {submitError}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          className="self-start"
          disabled={submitting || anyUploading}
          data-testid="custom-work-submit"
        >
          {submitting ? "Sending…" : "Request a quote"}
        </Button>
      </form>
    </main>
  );
}
